// Skeleton — light-gray loading block with subtle shimmer.
//
// Used in place of plain "Loading…" text on detail pages (matter, client,
// team member, judge, opposing counsel) so the chrome looks alive while
// data is in flight instead of like a dead page.
//
// Two presets:
//   <Skeleton />                     1-line block, full width
//   <Skeleton width={120} />         fixed pixel width
//   <Skeleton height={20} radius={6}/>
//   <SkeletonGroup>                  vertically-stacked rows that mimic a
//     <Skeleton />                   list/card layout — used by the
//     <Skeleton />                   detail-page loading screens.
//   </SkeletonGroup>
//
// Implementation note: animation lives in polish.css under .cc-skeleton so
// reduced-motion users get a static block, not a flashing one.

import type { CSSProperties, ReactNode } from "react";

interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number;
  className?: string;
  style?: CSSProperties;
}

export function Skeleton({ width, height = 14, radius, className, style }: SkeletonProps) {
  const merged: CSSProperties = {
    width: width ?? "100%",
    height,
    borderRadius: radius,
    ...style,
  };
  return (
    <span
      className={"cc-skeleton" + (className ? " " + className : "")}
      style={merged}
      aria-hidden="true"
    />
  );
}

interface SkeletonGroupProps {
  children: ReactNode;
  gap?: number;
  /**
   * If provided, used as the live screen-reader announcement so the
   * loading state isn't silent for assistive tech.
   */
  label?: string;
}

export function SkeletonGroup({ children, gap = 10, label = "Loading" }: SkeletonGroupProps) {
  return (
    <div
      className="cc-skeleton-group"
      style={{ display: "flex", flexDirection: "column", gap }}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      {children}
    </div>
  );
}

/**
 * Pre-baked card-shaped placeholder. Used by detail pages so each one
 * doesn't roll its own JSX for the same layout.
 */
export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="cc-card" style={{ padding: 18 }}>
      <SkeletonGroup>
        <Skeleton width="40%" height={18} />
        <Skeleton width="70%" height={12} />
        {Array.from({ length: Math.max(0, lines - 2) }).map((_, i) => (
          <Skeleton key={i} width={i % 2 === 0 ? "90%" : "75%"} height={12} />
        ))}
      </SkeletonGroup>
    </div>
  );
}
